import { useEffect, useState, useCallback } from "react";
import { Globe, Loader2, Play, Trash2, Plus, CheckCircle2, XCircle, Euro, RefreshCw, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

const TYPE_LABEL = {
    manufacturer: "🏭 Hersteller (Zubehör/UVP)",
    parts: "🔧 Ersatzteil-Quelle",
    supplier: "🚚 Lieferant (UVP-Fallback, Login)",
};
const STATUS_CFG = {
    corrected: { label: "Korrigiert", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    confirmed: { label: "Bestätigt", cls: "text-sky-700 bg-sky-50 border-sky-200" },
    no_uvp: { label: "Keine UVP", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    skipped: { label: "Übersprungen", cls: "text-gray-500 bg-gray-50 border-gray-200" },
};

// Aug-2026 (User-Wunsch): 3-Feld-Login für B2B-Portale (z. B. Niemann+Frey —
// Kundennummer + Benutzername + Passwort) direkt an der Lieferanten-Quelle.
function SupplierCreds({ source, onSaved }) {
    const [cno, setCno] = useState(source.customer_number || "");
    const [user, setUser] = useState(source.username || "");
    const [pwd, setPwd] = useState("");
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const body = { customer_number: cno.trim(), username: user.trim() };
            if (pwd.trim()) body.password = pwd.trim();
            await api.patch(`/dealer/research-sources/${source.id}`, body);
            toast.success("Zugangsdaten gespeichert");
            setPwd("");
            onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setSaving(false);
    };
    return (
        <div className="w-full flex items-center gap-1.5 flex-wrap pl-1 pt-1" data-testid={`supplier-creds-${source.domain}`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${source.has_credentials ? "text-emerald-700" : "text-amber-700"}`} data-testid={`supplier-creds-status-${source.domain}`}>
                {source.has_credentials
                    ? (source.credentials_scope === "branch" ? "✓ Standort-Login hinterlegt" : "✓ Login hinterlegt")
                    : (source.credentials_scope === "branch" ? "Standort-Login fehlt:" : "Login fehlt:")}
            </span>
            <input type="text" value={cno} onChange={(e) => setCno(e.target.value)} placeholder="Kundennummer"
                className="w-28 h-7 border border-gray-300 rounded-sm px-2 text-[11px]" data-testid={`supplier-creds-cno-${source.domain}`} />
            <input type="text" value={user} onChange={(e) => setUser(e.target.value)} placeholder="Benutzername" autoComplete="off"
                className="w-28 h-7 border border-gray-300 rounded-sm px-2 text-[11px]" data-testid={`supplier-creds-user-${source.domain}`} />
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                placeholder={source.has_credentials ? "Passwort (unverändert)" : "Passwort"} autoComplete="new-password"
                className="w-36 h-7 border border-gray-300 rounded-sm px-2 text-[11px]" data-testid={`supplier-creds-pwd-${source.domain}`} />
            <Button type="button" size="sm" variant="outline" onClick={save} disabled={saving}
                className="h-7 text-[10px] px-2" data-testid={`supplier-creds-save-${source.domain}`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Speichern"}
            </Button>
            {source.credentials_scope === "branch" && (
                <span className="w-full text-[10px] text-gray-500" data-testid={`supplier-creds-scope-${source.domain}`}>
                    Login <b>dieses Standorts</b> — Bestellungen laufen über das eigene Konto des Standorts.
                    {!source.has_credentials && source.hq_has_credentials && (
                        <b className="text-amber-700"> Die Zentrale hat einen Login, aber jeder Standort benötigt einen EIGENEN Zugang (sonst kommt die Ware am falschen Standort an)!</b>
                    )}
                </span>
            )}
        </div>
    );
}

export function SourcesSection() {
    const [sources, setSources] = useState(null);
    const [newDomain, setNewDomain] = useState("");
    const load = useCallback(() => {
        api.get("/dealer/research-sources").then((r) => setSources(r.data.sources || [])).catch(() => setSources([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = async (sid, upd) => {
        try { await api.patch(`/dealer/research-sources/${sid}`, upd); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };
    const add = async () => {
        if (!newDomain.trim()) return;
        try {
            await api.post("/dealer/research-sources", { domain: newDomain.trim(), type: "parts", brands: [] });
            setNewDomain(""); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    };
    const remove = async (sid) => { await api.delete(`/dealer/research-sources/${sid}`).catch(() => {}); load(); };

    if (!sources) return <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Quellen laden…</div>;
    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="research-sources-section">
            <div className="px-4 py-3 border-b border-gray-200">
                <div className="text-sm font-black flex items-center gap-2"><Globe className="w-4 h-4" /> Recherche-Quellen</div>
                <p className="text-[11px] text-gray-500 mt-0.5">
                    Zubehör-UVPs kommen <b>zuerst</b> von Hersteller-Quellen (mit exakter Artikelnummer-Prüfung). Wird dort nichts gefunden, prüft die KI das <b>Lieferanten-Portal</b> (z. B. Niemann+Frey, mit Login). Ersatzteil-Quellen liefern nur Daten — <b>nie</b> eine UVP.
                </p>
                <p className="text-[11px] font-semibold text-emerald-700" data-testid="uvp-sources-merged-hint">
                    Zusammengelegt mit der Artikeldaten-Anreicherung (WWS-Einstellungen): dieselbe Domain-Liste —
                    Hinzufügen/Entfernen wirkt automatisch in beiden Bereichen.
                </p>
            </div>
            <ul className="divide-y divide-gray-100">
                {sources.map((s) => (
                    <li key={s.id} className="px-4 py-2 flex items-center gap-2 flex-wrap text-xs" data-testid={`research-source-${s.domain}`}>
                        <span className="font-mono font-bold min-w-[190px]">{s.domain}</span>
                        <select
                            value={s.type}
                            onChange={(e) => patch(s.id, { type: e.target.value })}
                            className="h-7 border border-gray-300 rounded-sm px-1.5 text-[11px]"
                            data-testid={`research-source-type-${s.domain}`}
                        >
                            <option value="manufacturer">{TYPE_LABEL.manufacturer}</option>
                            <option value="parts">{TYPE_LABEL.parts}</option>
                            <option value="supplier">{TYPE_LABEL.supplier}</option>
                        </select>
                        {(s.type === "manufacturer" || s.type === "supplier") && (
                            <input
                                type="text"
                                defaultValue={(s.brands || []).join(", ")}
                                onBlur={(e) => patch(s.id, { brands: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                                placeholder="Marken, Komma-getrennt (z. B. Vespa)"
                                className="flex-1 min-w-[180px] h-7 border border-gray-300 rounded-sm px-2 text-[11px]"
                                data-testid={`research-source-brands-${s.domain}`}
                            />
                        )}
                        <button type="button" onClick={() => remove(s.id)} className="ml-auto text-gray-400 hover:text-red-600" title="Quelle entfernen" data-testid={`research-source-delete-${s.domain}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {s.type === "supplier" && (
                            <input
                                type="text"
                                defaultValue={s.search_url_template || ""}
                                onBlur={(e) => patch(s.id, { search_url_template: e.target.value.trim() })}
                                placeholder="Portal-Such-URL mit {query} als Platzhalter — für den Fahrzeug-Direktsprung aus dem Werkstattauftrag"
                                className="w-full h-7 border border-gray-300 rounded-sm px-2 text-[11px] font-mono"
                                data-testid={`research-source-searchurl-${s.domain}`}
                            />
                        )}
                        {s.type === "supplier" && <SupplierCreds source={s} onSaved={load} />}
                    </li>
                ))}
            </ul>
            <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
                <input
                    type="text" value={newDomain} onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="Neue Domain (z. B. shop.hersteller.de)"
                    className="flex-1 h-8 border border-gray-300 rounded-sm px-2 text-xs"
                    data-testid="research-source-new-domain"
                />
                <Button type="button" size="sm" variant="outline" onClick={add} className="h-8 text-xs" data-testid="research-source-add">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Hinzufügen
                </Button>
            </div>
        </div>
    );
}

function AuditSection() {
    const [job, setJob] = useState(null);
    const [starting, setStarting] = useState(false);
    const loadStatus = useCallback(() => {
        api.get("/dealer/uvp-audit/status").then((r) => setJob(r.data.job)).catch(() => {});
    }, []);
    useEffect(() => { loadStatus(); }, [loadStatus]);
    useEffect(() => {
        if (job?.status !== "running") return undefined;
        const t = setInterval(loadStatus, 4000);
        return () => clearInterval(t);
    }, [job?.status, loadStatus]);

    const start = async () => {
        setStarting(true);
        try {
            const r = await api.post("/dealer/uvp-audit/start", { limit: 40 });
            toast.success(`UVP-Prüfung gestartet — ${r.data.total} Artikel`);
            loadStatus();
        } catch (e) { toast.error(e.response?.data?.detail || "Start fehlgeschlagen"); }
        finally { setStarting(false); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="uvp-audit-section">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-sm font-black flex items-center gap-2"><Euro className="w-4 h-4" /> Zubehör-UVP-Prüfung</div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                        Recherchiert die UVP jedes Zubehör-Artikels auf der passenden Hersteller-Domain (exakte Artikelnummer) und korrigiert den VK automatisch — 40 Artikel pro Lauf.
                    </p>
                </div>
                <Button type="button" size="sm" onClick={start} disabled={starting || job?.status === "running"} className="ml-auto h-8 text-xs bg-[#111827] text-white" data-testid="uvp-audit-start">
                    {starting || job?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                    {job?.status === "running" ? "Läuft…" : "Jetzt prüfen"}
                </Button>
            </div>
            {job && (
                <div className="px-4 py-3 space-y-2" data-testid="uvp-audit-status">
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                        <span className="font-bold">{job.status === "running" ? "Läuft" : "Abgeschlossen"}: {job.done}/{job.total}</span>
                        <span className="text-emerald-700">✔ {job.corrected} korrigiert</span>
                        <span className="text-sky-700">{job.confirmed} bestätigt</span>
                        <span className="text-amber-700">{job.no_uvp} ohne UVP</span>
                        <span className="text-gray-500">{job.skipped} übersprungen</span>
                        {job.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-600 transition-all" style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }} />
                    </div>
                    {(job.log || []).length > 0 && (
                        <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-sm" data-testid="uvp-audit-log">
                            {[...(job.log || [])].reverse().map((l, i) => {
                                const cfg = STATUS_CFG[l.status] || STATUS_CFG.skipped;
                                return (
                                    <li key={i} className="px-2.5 py-1.5 text-[11px] flex items-center gap-2 flex-wrap">
                                        <span className={`font-bold text-[10px] uppercase border rounded-sm px-1.5 py-0.5 ${cfg.cls}`}>{cfg.label}</span>
                                        <span className="font-mono font-bold">{l.sku}</span>
                                        <span className="text-gray-600 truncate max-w-[220px]">{l.name}</span>
                                        {l.uvp != null && <span className="ml-auto font-mono-num">{l.old_vk != null && l.status === "corrected" ? `${Number(l.old_vk).toFixed(2)} € → ` : ""}<b>{Number(l.uvp).toFixed(2)} €</b></span>}
                                        {l.url && <a href={l.url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Quelle</a>}
                                        {l.note && <span className="text-gray-400">{l.note}</span>}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

function CatalogSection() {
    const [brands, setBrands] = useState([]);
    const [brand, setBrand] = useState("Vespa");
    const [job, setJob] = useState(null);
    const [items, setItems] = useState([]);
    const [busyId, setBusyId] = useState(null);

    const loadAll = useCallback(() => {
        api.get("/dealer/catalog-sync/status").then((r) => setJob(r.data.job)).catch(() => {});
        api.get("/dealer/catalog-review").then((r) => setItems(r.data.items || [])).catch(() => {});
        api.get("/dealer/research-sources").then((r) => {
            const bs = [...new Set((r.data.sources || []).filter((s) => s.type === "manufacturer").flatMap((s) => s.brands || []))];
            setBrands(bs);
            if (bs.length && !bs.includes("Vespa")) setBrand(bs[0]);
        }).catch(() => {});
    }, []);
    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => {
        if (job?.status !== "running") return undefined;
        const t = setInterval(loadAll, 5000);
        return () => clearInterval(t);
    }, [job?.status, loadAll]);

    const start = async () => {
        try {
            await api.post("/dealer/catalog-sync/start", { brand });
            toast.success(`Katalog-Abgleich ${brand} gestartet`);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Start fehlgeschlagen"); }
    };
    const decide = async (rid, action) => {
        setBusyId(rid);
        try {
            await api.post(`/dealer/catalog-review/${rid}`, { action });
            toast.success(action === "approve" ? "Artikel angelegt (UVP-gebunden)" : "Verworfen");
            setItems((xs) => xs.filter((x) => x.id !== rid));
        } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
        finally { setBusyId(null); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="catalog-sync-section">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 flex-wrap">
                <div className="min-w-0">
                    <div className="text-sm font-black flex items-center gap-2"><PackageSearch className="w-4 h-4" /> Hersteller-Katalog-Abgleich</div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                        Findet Zubehör auf der Herstellerseite, das noch nicht im System ist — neue Artikel landen in der Review-Liste zum Bestätigen.
                    </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <select value={brand} onChange={(e) => setBrand(e.target.value)} className="h-8 border border-gray-300 rounded-sm px-2 text-xs" data-testid="catalog-sync-brand">
                        {(brands.length ? brands : ["Vespa", "Piaggio", "Aprilia", "Moto Guzzi"]).map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <Button type="button" size="sm" onClick={start} disabled={job?.status === "running"} className="h-8 text-xs bg-[#111827] text-white" data-testid="catalog-sync-start">
                        {job?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                        {job?.status === "running" ? "Läuft…" : "Abgleich starten"}
                    </Button>
                </div>
            </div>
            {job && (
                <div className="px-4 py-2 text-[11px] text-gray-600 border-b border-gray-100" data-testid="catalog-sync-status">
                    Letzter Lauf ({job.brand}): {job.status === "running" ? "läuft" : "fertig"} — {job.pages || 0} Seiten geprüft, <b className="text-emerald-700">{job.found_new || 0} neue Artikel</b>, {job.existing || 0} bereits vorhanden
                </div>
            )}
            <div className="px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Review-Liste ({items.length})</div>
                {items.length === 0 ? (
                    <div className="text-xs text-gray-400" data-testid="catalog-review-empty">Keine offenen Vorschläge.</div>
                ) : (
                    <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto border border-gray-100 rounded-sm" data-testid="catalog-review-list">
                        {items.map((it) => (
                            <li key={it.id} className="px-2.5 py-1.5 text-[11px] flex items-center gap-2 flex-wrap" data-testid={`catalog-review-item-${it.part_number}`}>
                                <span className="font-mono font-bold">{it.part_number}</span>
                                <span className="text-gray-600 truncate max-w-[230px]">{it.name}</span>
                                <span className="text-gray-400">{it.brand}</span>
                                {it.uvp != null ? <span className="font-mono-num font-bold">{Number(it.uvp).toFixed(2)} €</span> : <span className="text-amber-600">ohne UVP</span>}
                                {it.source_url && <a href={it.source_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Quelle</a>}
                                <span className="ml-auto flex items-center gap-1">
                                    <button type="button" onClick={() => decide(it.id, "approve")} disabled={busyId === it.id} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-2 py-1" data-testid={`catalog-review-approve-${it.part_number}`}>
                                        <CheckCircle2 className="w-3 h-3" /> Anlegen
                                    </button>
                                    <button type="button" onClick={() => decide(it.id, "reject")} disabled={busyId === it.id} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-red-700 border border-red-300 hover:bg-red-50 rounded-sm px-2 py-1" data-testid={`catalog-review-reject-${it.part_number}`}>
                                        <XCircle className="w-3 h-3" /> Verwerfen
                                    </button>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function UvpResearchPanel() {
    return (
        <div className="space-y-4" data-testid="uvp-research-panel">
            <SourcesSection />
            <AuditSection />
            <CatalogSection />
        </div>
    );
}
